import { QRDesign, QRHealthReport, QRHealthIssue } from '@/types/qr';

/**
 * Parses a hex, rgb, or named color into RGB components.
 */
function hexToRgb(colorStr: string): { r: number; g: number; b: number } {
  const clean = colorStr.trim().toLowerCase();
  
  if (clean.startsWith('#')) {
    let hex = clean.substring(1);
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
    if (hex.length === 6 || hex.length === 8) {
      const num = parseInt(hex.substring(0, 6), 16);
      return {
        r: (num >> 16) & 255,
        g: (num >> 8) & 255,
        b: num & 255
      };
    }
  }

  // Fallback defaults
  if (clean === 'transparent') {
    return { r: 255, g: 255, b: 255 }; // Treat transparent background as light for estimation
  }

  return { r: 0, g: 0, b: 0 };
}

/**
 * Calculates relative luminance according to WCAG 2.1 specs.
 */
function getRelativeLuminance(rgb: { r: number; g: number; b: number }): number {
  const sRGB = [rgb.r, rgb.g, rgb.b].map(v => {
    const val = v / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];
}

/**
 * Calculates contrast ratio between two colors (1:1 to 21:1).
 */
export function getContrastRatio(foreground: string, background: string): number {
  const fgRgb = hexToRgb(foreground);
  const bgRgb = hexToRgb(background);

  const l1 = getRelativeLuminance(fgRgb);
  const l2 = getRelativeLuminance(bgRgb);

  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Evaluates the QR design, payload length, and logo to produce a comprehensive health and scanability report.
 */
export function evaluateQRHealth(
  rawText: string,
  design: QRDesign
): QRHealthReport {
  const issues: QRHealthIssue[] = [];
  let score = 100;
  const dataLength = rawText ? new TextEncoder().encode(rawText).length : 0;

  // 1. Contrast Check
  const effectiveBg = design.isTransparent ? '#ffffff' : design.backgroundColor;
  const contrast = getContrastRatio(design.dotsColor, effectiveBg);

  if (contrast < 3.0) {
    score -= 40;
    issues.push({
      id: 'contrast-critical',
      level: 'critical',
      title: 'Very Low Color Contrast',
      description: `Contrast ratio is ${contrast.toFixed(1)}:1 (minimum recommended is 4.5:1). QR scanners may fail to recognize code modules.`,
      recommendation: 'Use a darker foreground color or lighter background color for optimal optical contrast.'
    });
  } else if (contrast < 4.5) {
    score -= 20;
    issues.push({
      id: 'contrast-warning',
      level: 'warning',
      title: 'Suboptimal Contrast',
      description: `Contrast ratio is ${contrast.toFixed(1)}:1. Some smartphone cameras in low light may struggle to scan.`,
      recommendation: 'Increase contrast between modules and background.'
    });
  }

  // 2. Data Density / Version Estimate Check
  // Rough QR version calculation
  let estimatedVersion = 1;
  if (dataLength > 500) estimatedVersion = 18;
  else if (dataLength > 300) estimatedVersion = 12;
  else if (dataLength > 150) estimatedVersion = 7;
  else if (dataLength > 60) estimatedVersion = 4;
  else estimatedVersion = 2;

  if (dataLength > 600) {
    score -= 25;
    issues.push({
      id: 'density-dense',
      level: 'warning',
      title: 'High Payload Density',
      description: `Payload is ${dataLength} bytes. The resulting QR code contains a very dense grid of small modules.`,
      recommendation: 'Shorten the URL or content, or export at 1024px+ resolution if printing.'
    });
  } else if (dataLength > 250) {
    score -= 10;
    issues.push({
      id: 'density-moderate',
      level: 'info',
      title: 'Moderate Data Density',
      description: `Payload is ${dataLength} bytes. Ensure adequate quiet zone margin when printing.`,
      recommendation: 'Test scanability with your device camera.'
    });
  }

  // 3. Quiet Zone (Margin) Check
  if (design.margin < 5) {
    score -= 15;
    issues.push({
      id: 'margin-small',
      level: 'warning',
      title: 'Very Small Quiet Zone',
      description: 'The margin around the QR code is very small (less than 1 module width).',
      recommendation: 'Increase the margin slider to at least 10px so cameras can distinguish QR boundaries.'
    });
  }

  // 4. Logo Occlusion & Error Correction Check
  if (design.logo?.dataUrl) {
    if (design.errorCorrection !== 'H' && design.errorCorrection !== 'Q') {
      score -= 25;
      issues.push({
        id: 'logo-ec-low',
        level: 'warning',
        title: 'Error Correction Level Too Low for Logo',
        description: `Your QR code contains a logo, but error correction is set to '${design.errorCorrection}' (${design.errorCorrection === 'L' ? '7%' : '15%'} recovery capacity).`,
        recommendation: 'Switch Error Correction level to H (30%) to guarantee reliable scanning with embedded logos.'
      });
    }

    if (design.logo.size > 0.3) {
      score -= 15;
      issues.push({
        id: 'logo-size-large',
        level: 'warning',
        title: 'Large Logo Area',
        description: 'The embedded logo occupies a large portion of the QR center modules.',
        recommendation: 'Reduce the logo size slider to 20-25% to prevent blocking critical data codewords.'
      });
    }
  }

  // Final Health Score & Status mapping
  const finalScore = Math.max(0, Math.min(100, score));
  let status: 'excellent' | 'good' | 'warning' | 'critical' = 'excellent';
  if (finalScore < 45 || issues.some(i => i.level === 'critical')) {
    status = 'critical';
  } else if (finalScore < 75 || issues.some(i => i.level === 'warning')) {
    status = 'warning';
  } else if (finalScore < 90) {
    status = 'good';
  }

  return {
    score: finalScore,
    status,
    contrastRatio: contrast,
    dataLength,
    estimatedVersion,
    issues
  };
}
